import { useCallback, useRef, useState, type FocusEvent, type PointerEvent } from 'react'

export type TooltipState = { x: number; y: number; value: string; label: string } | null

/**
 * Hover/focus tooltip for a chart. Attach `ref` to the chart's wrapper (position: relative),
 * then spread `bind(value, label)` onto each mark. Works with the mouse and the keyboard.
 */
export function useTooltip<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [tip, setTip] = useState<TooltipState>(null)

  const place = useCallback((clientX: number, clientY: number, value: string, label: string) => {
    const box = ref.current?.getBoundingClientRect()
    if (!box) return
    setTip({ x: clientX - box.left, y: clientY - box.top, value, label })
  }, [])

  const bind = (value: string, label: string) => ({
    tabIndex: 0,
    'aria-label': `${label}: ${value}`,
    onPointerMove: (e: PointerEvent) => place(e.clientX, e.clientY, value, label),
    onPointerLeave: () => setTip(null),
    onFocus: (e: FocusEvent<Element>) => {
      const r = e.currentTarget.getBoundingClientRect()
      place(r.left + r.width / 2, r.top, value, label)
    },
    onBlur: () => setTip(null),
  })

  return { ref, tip, bind }
}
